import React, { useEffect, useMemo, useState } from "react";
import Grid from "@mui/material/Grid";
import MediaCard from "./MediaCard";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import { fetchDashboardData } from "../../api/dashboardService";
import { fetchAuthSession } from "aws-amplify/auth";

export default function Dashboard() {
  const [dashboards, setDashboards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      setError(null);
      try {
        const session = await fetchAuthSession();
        const token = session.tokens.idToken;
        if (!token) throw new Error("User token is missing. Please log in.");
        const result = await fetchDashboardData(token);
        if (!result.success) {
          throw new Error("Failed to fetch dashboard data");
        }
        setDashboards(result.dashboards || []);
      } catch (err) {
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to fetch dashboard data"
        );
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  // Group dashboards by tile_group
  const groupedData = useMemo(() => {
    if (!dashboards || dashboards.length === 0) return {};

    const grouped = dashboards.reduce((acc, item) => {
      const groupKey = item.tile_group || "Uncategorized";
      if (!acc[groupKey]) acc[groupKey] = [];
      acc[groupKey].push(item);
      return acc;
    }, {});

    const sortedKeys = Object.keys(grouped).sort();
    const sortedGrouped = {};
    sortedKeys.forEach((key) => {
      sortedGrouped[key] = grouped[key];
    });
    return sortedGrouped;
  }, [dashboards]);
  console.log("Grouped Data:", groupedData);

  return (
    <>
      {loading && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh", // Full viewport height
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: "1.25rem" }}>Loading dashboards...</div>
        </div>
      )}
      {error && (
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          style={{ height: "100%" }}
        >
          <Alert severity="error">{error}</Alert>
        </Grid>
      )}
      {!loading && !error && Object.keys(groupedData).length > 0 && (
        <Grid
          container
          sx={{
            mt: 4,
            mb: 4,
            padding: 2, // Use padding instead of margins
            gap: 2, // Use gap instead of spacing
            overflowY: "scroll",
          }}
          alignItems={
            Object.keys(groupedData).length === 1 ? "flex-start" : "stretch"
          }
        >
          {Object.entries(groupedData).map(([workbookname, items], index) => (
            <Grid container item key={index} sx={{ width: "100%" }}>
              <React.Fragment key={index}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    {workbookname}
                  </Typography>
                  <Divider />
                </Grid>
                <Grid container item sx={{ gap: 3, mb: 6 }}>
                  {items.map((item, idx) => (
                    <Grid
                      item
                      xs={12}
                      md={4}
                      lg={3}
                      key={idx}
                      sx={{
                        padding: { md: 2, lg: 3 }, // Add padding for medium and large screens
                      }}
                    >
                      <MediaCard data={item} />
                    </Grid>
                  ))}
                </Grid>
              </React.Fragment>
            </Grid>
          ))}
        </Grid>
      )}
      {!loading && !error && Object.keys(groupedData).length === 0 && (
        <Typography variant="body1" sx={{ mt: 4, ml: 4 }}>
          No dashboards available.
        </Typography>
      )}
    </>
  );
}
